import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Stack,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import parseSchemaDocs from '../../../shared/molecules/CustomEditor/utils/parseSchemaDocs';
import { primaryColorObj } from '../../../../themes';
import { convertHexToRGBA } from '../../../../utils/convertHexToRGBA';

const DocumentationView = ({ schema }) => {
  const docs = useMemo(() => parseSchemaDocs(schema), [schema]);

  const primaryTextColor = '#e0f7fa';
  const secondaryTextColor = '#b2ebf2';

  return (
    <Box height={360} sx={{ overflowY: 'auto' }} paddingY="1rem">
      <Stack spacing={3} pr={2} sx={{ position: 'relative' }}>
        {docs.map((d, i) => {
          // Divider before entering a new depth level
          const prevDoc = docs[i - 1] ?? { depth: -1, depthName: null };
          const showDividerAbove =
            d.depth > 0 &&
            (d.depth > prevDoc.depth || d.depthName !== prevDoc.depthName);

          return (
            <React.Fragment key={d.path}>
              {showDividerAbove && (
                <Divider
                  textAlign="left"
                  sx={{
                    color: primaryColorObj.default,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    mb: 1,
                    '&::before, &::after': {
                      borderColor: convertHexToRGBA(primaryColorObj, '0.3')
                        .default,
                    },
                    textTransform: 'uppercase',
                  }}
                >
                  {d.depthName}
                </Divider>
              )}

              <Card
                sx={{
                  borderColor: 'rgba(255,255,255,0.08)',
                  background:
                    'linear-gradient(145deg, rgba(255,255,255,0.10), rgba(255,255,255,0.08))',
                  textAlign: 'left',
                  boxShadow: 'none',
                  '& *': {
                    fontSize: '1rem',
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: primaryTextColor,
                      textShadow: '0 0 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    {d.title ?? d.path}
                  </Typography>

                  {d.title && (
                    <Typography
                      variant="subtitle2"
                      sx={{ color: secondaryTextColor, mt: 0.25 }}
                    >
                      {d.path}
                    </Typography>
                  )}

                  {d.description && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: primaryTextColor }}
                    >
                      {d.description}
                    </Typography>
                  )}

                  {d.comment && (
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        display: 'block',
                        mt: 0.5,
                        color: secondaryTextColor,
                      }}
                    >
                      {d.comment}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
};

DocumentationView.propTypes = {
  schema: PropTypes.object,
};

export default DocumentationView;
